import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { useFormik } from "formik";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Tooltip from "@mui/material/Tooltip";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import PublishIcon from "@mui/icons-material/Publish";
import LoopIcon from "@mui/icons-material/Loop";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import {
    PlannedWorkShiftDto,
    PlannedWorkShiftSaveDto,
    PlannedWorkShiftState,
} from "../../models/plannedWorkShift/plannedWorkShift";
import { useTranslation } from "react-i18next";
import { deletePlannedWorkShift } from "../../services/plannedWorkShiftService";
import { useAppDispatch, useAppSelector } from "../../framework/customStore";
import {
    clearConfirmation,
    setConfirmation,
    showErrorMessage,
    showSuccessMessage,
} from "../../models/store/storeActions";
import { reloadPlannedWorkShifts } from "../../store/workShiftPlanningSlice";
import { selectShiftOverlapsById } from "../../store/selectors/workShiftOverlapSelectors";
import { LoadingIndicator } from "../framework/loadingIndicatorNew";
import { ConfirmationDialogType } from "../../models/store/storeTypes";
import { VehicleEditor, EmployeeEditor, RouteEditor } from "./WorkShiftFieldEditors";
import { TextInlineEdit } from "./TextInlineEdit";
import { TimePickerInlineEdit } from "./TimePickerInlineEdit";
import {
    createRecurringShiftInstance,
    deleteRecurringShift,
    editRecurringShift,
} from "../../services/recurringShiftService";
import { Base } from "../../framework/base";
import {
    DATE_COLUMN_WIDTH,
    TimelineGridContext,
} from "../timelinePlanner/timelinePlannerGrid";
import { RecurrenceAction } from "../../models/common/enums";
import { ApiError } from "../../services/baseService";
import { useSavePlannedWorkShiftWithFeedback } from "../../hooks/workShiftPlanning/useSavePlannedWorkShiftWithFeedback";
import { usePublishShifts } from "../../hooks/workShiftPlanning/usePublishShifts";
import {
    RecurringShiftInstanceType,
    RepeatEnabledDays16,
} from "../../models/recurringShift/recurringShift";
import MuiMenu, { IMuiMenuItemProps } from "../framework/muiMenu";
import { ShiftCardCompact } from "./ShiftCardCompact";
import { RouteDialog } from "./RouteDialog";
import { RouteSaveDto } from "../../models/route/route";
import { buildOverlapTooltip, tooltipProps } from "./utils/OverlapTooltip";
import RecurrenceDialog from "./RecurrenceDialog";
import { shiftColors, ColorCriterion } from "./utils/ShiftColors";
import { selectColorSchemeByScope } from "../../store/colorSchemeSlice";
import { WorkShiftDtoBasic } from "../../models/workShift/workShift";
import { WorkShiftChips } from "./WorkShiftChips";

const weekDays = [
    { labelKey: "common.monday", value: RepeatEnabledDays16.Monday1 },
    { labelKey: "common.tuesday", value: RepeatEnabledDays16.Tuesday1 },
    { labelKey: "common.wednesday", value: RepeatEnabledDays16.Wednesday1 },
    { labelKey: "common.thursday", value: RepeatEnabledDays16.Thursday1 },
    { labelKey: "common.friday", value: RepeatEnabledDays16.Friday1 },
    { labelKey: "common.saturday", value: RepeatEnabledDays16.Saturday1 },
    { labelKey: "common.sunday", value: RepeatEnabledDays16.Sunday1 },
];
interface WorkShiftTimelineCellProps {
    shift: PlannedWorkShiftDto;
    actualShifts?: WorkShiftDtoBasic[];
    isSelected?: boolean;
    onClick?: (event: React.MouseEvent) => void;
    isCompact?: boolean;
    topOffset: number;
    horizontalPadding?: number;
    onHeightChange?: (shiftId: string, height: number) => void;
}

export const getRecurrenceDeleteAction = (
    action: RecurrenceAction,
    shift: PlannedWorkShiftDto
) => {
    if (!action) {
        // Delete the single planned shift
        return deletePlannedWorkShift(shift.id);
    } else if (action === RecurrenceAction.OnlyThis) {
        // Delete this planned shift only if instance already overridden or generated
        if (
            shift.isOverridenFromRecurrence ||
            shift.isGeneratedFromRecurrence
        ) {
            return deletePlannedWorkShift(shift.id);
        }

        return createRecurringShiftInstance({
            recurringShiftId: shift.recurrence.recurringShiftId,
            recurringShiftRowId: shift.recurrence.rowId,
            date: Base.dayjsToJsonDate(shift.startTime),
            type: RecurringShiftInstanceType.Skip,
        });
    } else if (action === RecurrenceAction.AllFuture) {
        return editRecurringShift(shift.recurrence.recurringShiftId, {
            rowId: shift.recurrence.rowId,
            startDate: shift.recurrence.recurrenceStartDate,
            intervalInWeeks: shift.recurrence.intervalInWeeks,
            repeatDays: shift.recurrence.repeatDays,
            startTime: shift.recurrence.startTime,
            endTime: shift.recurrence.endTime,
            label: shift.recurrence.label,
            employeeId: shift.recurrence.employeeId,
            vehicleId: shift.recurrence.vehicleId,
            routeId: shift.recurrence.routeId,
            comment: shift.recurrence.comment,
            endDate: Base.dayjsToJsonDate(
                Base.dateAddDays(shift.startTime, -1)
            ),
        });
    } else {
        return deleteRecurringShift(shift.recurrence.recurringShiftId);
    }
};

const GRID_CELL_VERTICAL_PADDING = 0;
const DEFAULT_HORIZONTAL_PADDING = 4;

export const WorkShiftTimelineCell = ({
    shift,
    actualShifts,
    isSelected = false,
    onClick,
    isCompact = false,
    topOffset,
    horizontalPadding = DEFAULT_HORIZONTAL_PADDING,
    onHeightChange,
}: WorkShiftTimelineCellProps) => {
    const [loading, setLoading] = useState(false);
    const { startTime, endTime, state } = shift;
    const { pathname } = useLocation();
    const history = useHistory();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const context = useContext(TimelineGridContext);
    const containerRef = useRef<HTMLDivElement | null>(null);

    const { publishSingleShift } = usePublishShifts();
    const [createRouteOpen, setCreateRouteOpen] = useState(false);
    const newRouteInitial: RouteSaveDto = useMemo(() => ({ id: null, rowId: null, name: "", comment: null, isActive: true }), []);

    const startIsInRange = useMemo(
        () => !!context?.getColumnForDate(startTime, true),
        [context?.getColumnForDate, startTime]
    );
    const endIsInRange = useMemo(
        () => !!context?.getColumnForDate(endTime, true),
        [context?.getColumnForDate, endTime]
    );

    const isMultiDayShift = useMemo(() => {
        const startDate = startTime?.substring(0, 10);
        const endDate = endTime?.substring(0, 10);
        return startDate !== endDate;
    }, [startTime, endTime]);

    const adjustedEndTime = useMemo(() => {
        if (!isMultiDayShift) return endTime;

        const startDay = startTime?.substring(0, 10);
        return `${startDay}T23:59:59`;
    }, [isMultiDayShift, startTime, endTime]);

    const actualShiftsDuration = useMemo(
        () =>
            actualShifts
                ?.filter((s) =>
                    Base.isOverlappingDate(
                        startTime,
                        endTime,
                        // Actual shift timezone should not matter, only timestamps need to overlap
                        Base.dateStrToDayjsIgnoreTimezone(s.startTime),
                        Base.dateStrToDayjsIgnoreTimezone(s.endTime),
                    ),
                )
                .reduce(
                    (acc, cur) =>
                        acc +
                        Base.dayjsDiffInMinutes(cur.startTime, cur.endTime),
                    0,
                ),
        [actualShifts, startTime, endTime],
    );

    const plannedDuration = useMemo(
        () => Base.dayjsDiffInMinutes(startTime, endTime),
        [startTime, endTime],
    );

    const overlapsById = useAppSelector(selectShiftOverlapsById);
    const overlapEntry = overlapsById[shift.id];
    const hasConflict =
        !!overlapEntry &&
        ((overlapEntry.employeeConflicts?.length ?? 0) > 0 ||
            (overlapEntry.vehicleConflicts?.length ?? 0) > 0);

    const allShifts = useAppSelector(
        (state) => state.workShiftPlanning?.plannedWorkShifts ?? []
    );

    const overlapTooltip =
        hasConflict
            ? buildOverlapTooltip(
                [
                    ...(overlapEntry?.employeeConflicts ?? []),
                    ...(overlapEntry?.vehicleConflicts ?? []),
                ],
                allShifts,
                t
            )
            : null;

    // Save exception for recurrence if this is from a recurring shift.
    const submit = useSavePlannedWorkShiftWithFeedback(
        shift,
        shift.isTempInstance ? RecurrenceAction.OnlyThis : null
    );

    const handleSave = (
        newValue: string,
        fieldName: keyof PlannedWorkShiftSaveDto
    ): Promise<void> => Promise.resolve(submit({ ...shift, [fieldName]: newValue }));

    const handleEditShift = () => {
        history.push(`${pathname}/edit/${shift.id}`);
    };

    const handleEditRecurring = (action: RecurrenceAction) => {
        history.push({
            pathname: `${pathname}/edit/${shift.id}/${action}`,
            // If instance has not been created, set shift to state
            state: shift.isTempInstance ? { shift } : undefined,
        });
    };

    const [recurrenceMenuEl, setRecurrenceMenuEl] = useState<HTMLElement | null>(null);
    const [recurrenceAction, setRecurrenceAction] = useState<RecurrenceAction | null>(null);
    const [recurrenceDialogOpen, setRecurrenceDialogOpen] = useState(false);

    const submitRecurrence = useSavePlannedWorkShiftWithFeedback(
        shift,
        recurrenceAction,
        () => {
            setRecurrenceDialogOpen(false);
            dispatch(reloadPlannedWorkShifts());
        },
        () => {}
    );

    const recurrenceInitialValues = useMemo(() => {
        const recurrenceVal = shift.recurrence ? { ...shift.recurrence } : undefined;

        if (recurrenceVal && recurrenceAction === RecurrenceAction.AllFuture) {
            recurrenceVal.recurrenceStartDate = Base.dayjsToJsonDate(shift.startTime);
        }

        return {
            startTime: shift.startTime,
            endTime: shift.endTime,
            recurrence: recurrenceVal,
        } as PlannedWorkShiftSaveDto;
    }, [recurrenceAction, shift.endTime, shift.recurrence, shift.startTime]);

    const recurrenceFormik = useFormik<PlannedWorkShiftSaveDto>({
        enableReinitialize: true,
        initialValues: recurrenceInitialValues,
        onSubmit: (values) => {
            submitRecurrence({
                ...shift,
                recurrence: values.recurrence,
            });
        },
    });

    const columnMetrics = useMemo(() => {
        if (!context) {
            return null;
        }

        const startColumn = context.getColumnForDate(startTime);
        if (!startColumn) {
            return null;
        }

        const effectiveEnd = isMultiDayShift ? adjustedEndTime : endTime;
        const endColumn = context.getColumnForDate(effectiveEnd) || startColumn;
        const span = Math.max(1, endColumn - startColumn + 1);
        const left = (startColumn - 1) * DATE_COLUMN_WIDTH + horizontalPadding;
        const width = Math.max(
            0,
            span * DATE_COLUMN_WIDTH - horizontalPadding * 2
        );

        return { left, width };
    }, [context, startTime, endTime, adjustedEndTime, isMultiDayShift, horizontalPadding]);

    useEffect(() => {
        if (!containerRef.current || !onHeightChange) {
            return;
        }

        const element = containerRef.current;
        const notify = () => {
            onHeightChange(shift.id, element.getBoundingClientRect().height);
        };

        notify();

        if (typeof ResizeObserver === "undefined") {
            return;
        }

        const observer = new ResizeObserver(() => notify());
        observer.observe(element);

        return () => observer.disconnect();
    }, [onHeightChange, shift.id]);

    if (!columnMetrics) {
        return <LoadingIndicator loading={loading} />;
    }

    const handleOpenRecurrenceEditor = (e?: React.MouseEvent) => {
        e?.stopPropagation();
        setRecurrenceMenuEl(e?.currentTarget as HTMLElement);
    };
    const handleCloseRecurrenceMenu = () => setRecurrenceMenuEl(null);
    const handleSelectRecurrence = (action: RecurrenceAction) => {
        handleCloseRecurrenceMenu();
        if (action === RecurrenceAction.OnlyThis) {
            handleEditRecurring(action);
            return;
        }
        setRecurrenceAction(action);
        setRecurrenceDialogOpen(true);
    };

    const isRecurring = !!shift.recurrence;

    const recurrenceLabel = useMemo(() => {
        if (!isRecurring) return null;

        const recurrence = shift.recurrence;
        const repeatsDaily = recurrence.repeatDays === 127;

        let frequencyLabel = repeatsDaily
            ? recurrence.intervalInWeeks > 1
                ? t("workShiftPlanning.recurring.everyXWeekDaily", {
                    count: recurrence.intervalInWeeks,
                })
                : t("workShiftPlanning.recurring.daily")
            : recurrence.intervalInWeeks > 1
                ? t("workShiftPlanning.recurring.everyXWeekLabel", {
                    count: recurrence.intervalInWeeks,
                })
                : t("workShiftPlanning.recurring.everyWeekLabel");

        const weekdayLabel = !repeatsDaily
            ? weekDays
                .filter((w) => recurrence.repeatDays & w.value)
                .map((w) =>
                    Base.UncapitalizeFirstChar(t(w.labelKey).slice(0, 2))
                )
                .join(", ")
            : "";

        const dateRangeLabel =
            recurrence.recurrenceStartDate && recurrence.recurrenceEndDate
                ? `${Base.dayjsToDateStr(
                    recurrence.recurrenceStartDate
                )} - ${Base.dayjsToDateStr(
                    recurrence.recurrenceEndDate
                )}`
                : recurrence.recurrenceStartDate
                    ? t("workShiftPlanning.recurring.startingFrom", {
                        date: Base.dayjsToDateStr(
                            recurrence.recurrenceStartDate
                        ),
                    })
                    : "";

        if (shift.isOverridenFromRecurrence) {
            frequencyLabel = Base.UncapitalizeFirstChar(frequencyLabel);
            return `${t("workShiftPlanning.recurring.overridden")}. ` +
                t("workShiftPlanning.recurring.originalSeriesRepeats", {
                    frequency: frequencyLabel,
                    weekdays: weekdayLabel,
                    date: dateRangeLabel,
                });
        }

        return [frequencyLabel, weekdayLabel, dateRangeLabel]
            .filter(Boolean)
            .join(" ");
    }, [isRecurring, shift.isOverridenFromRecurrence, shift.recurrence]);

    const handleDeleteShift = (recurrenceAction?: RecurrenceAction) =>
        dispatch(
            setConfirmation(
                ConfirmationDialogType.Warning,
                t("common.warning"),
                !recurrenceAction
                    ? t("workShiftPlanning.confirmDeleteShift")
                    : t(
                          `workShiftPlanning.recurring.confirmDelete.${recurrenceAction}`
                    ),
                () => {
                    dispatch(clearConfirmation());
                    setLoading(true);

                    getRecurrenceDeleteAction(recurrenceAction, shift)
                        .then(() => {
                            dispatch(
                                showSuccessMessage(t("common.deleteSuccess"))
                            );
                            dispatch(reloadPlannedWorkShifts());
                        })
                        .catch((err: ApiError) =>
                            dispatch(
                                showErrorMessage(
                                    err?.status === 409
                                        ? t("common.saveConflict")
                                        : t("common.saveFailed")
                                )
                            )
                        )
                        .finally(() => setLoading(false));
                },
                () => dispatch(clearConfirmation())
            )
        );

    const handlePublishShift = () => {
        if (state === PlannedWorkShiftState.InPlanning) {
            setLoading(true);
            void publishSingleShift(shift)
                .finally(() => setLoading(false));
        }
    };

    const timeLabel = useMemo(() => {
        const startIsTime = startIsInRange;
        const endIsTime = endIsInRange;
        const startStr = startIsTime
            ? Base.dayjsToTimeStr(shift.startTime)
            : Base.dayjsToShortDateTimeStr(shift.startTime);
        const endStr = isMultiDayShift
            ? Base.dayjsToShortDateTimeStr(shift.endTime)
            : endIsTime
                ? Base.dayjsToTimeStr(shift.endTime)
                : Base.dayjsToShortDateTimeStr(shift.endTime);
        return `${startStr} - ${endStr}`;
    }, [shift.startTime, shift.endTime, startIsInRange, endIsInRange, isMultiDayShift]);

    const showPublishButton = state === PlannedWorkShiftState.InPlanning;
    const menuItems: IMuiMenuItemProps[] = [];

    // Publish (always show when in planning; disabled if no employee)
    if (showPublishButton) {
        const canPublish = !!shift.employeeId;
        menuItems.push({
            label: t("workShiftPlanning.publishShift"),
            icon: (
                <PublishIcon
                    fontSize="small"
                    sx={{ color: canPublish ? "primary.main" : "text.disabled" }}
                />
            ),
            onClick: canPublish ? handlePublishShift : undefined,
            disabled: !canPublish,
            tooltip: !canPublish
                ? t("workShiftPlanning.tooltips.publishOnlyWithEmployee")
                : undefined,
        });
        menuItems.push({ divider: true });
    }

    // Edit
    if (isRecurring) {
        Object.values(RecurrenceAction).forEach((action) => {
            menuItems.push({
                id: `workshift-recurring-edit-${shift.id}-${action}`,
                label: t(`workShiftPlanning.recurring.recurrenceActionLabel.${action}`),
                icon: <EditIcon fontSize="small" />,
                onClick: () => handleEditRecurring(action)
            });
        });
    } else {
        menuItems.push({
            id: `workshift-edit-${shift.id}`,
            label: t("common.edit"),
            icon: <EditIcon fontSize="small" />,
            onClick: handleEditShift
        });
    }

    menuItems.push({ divider: true });

    // Delete
    if (isRecurring) {
        Object.values(RecurrenceAction).forEach((action) => {
            menuItems.push({
                id: `workshift-recurring-delete-${shift.id}-${action}`,
                label: t(`workShiftPlanning.recurring.recurrenceActionLabel.${action}`),
                icon: <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />,
                onClick: () => handleDeleteShift(action)
            });
        });
    } else {
        menuItems.push({
            id: `workshift-delete-${shift.id}`,
            label: t("common.remove"),
            icon: <DeleteIcon fontSize="small" sx={{ color: "error.main" }} />,
            onClick: () => handleDeleteShift()
        });
    }

    const colorScheme = useAppSelector(selectColorSchemeByScope("workShiftPlanning"));
    const isColorCodingOn = !!colorScheme?.enabled && !!colorScheme?.mappings && Object.keys(colorScheme.mappings).length > 0;

    let criterionId: string | null = null;
    if (isColorCodingOn && colorScheme) {
        if (colorScheme.criterion === ColorCriterion.Employee) criterionId = shift.employeeId;
        else if (colorScheme.criterion === ColorCriterion.Vehicle) criterionId = shift.vehicleId;
        else criterionId = shift.routeId;
    }

    const computeColorKey = () => {
        if (!isColorCodingOn || !colorScheme) return null;
        if (!criterionId) return null;
        return colorScheme.mappings[criterionId] ?? colorScheme.defaultColorKey ?? "blue";
    };

    const colorKey = computeColorKey();
    let backgroundColor: string;

    if (isColorCodingOn) {
        if (!criterionId) {
            backgroundColor = state === PlannedWorkShiftState.Published ? "#d4d4d4" : "#e0e0e0";
        } else if (colorKey) {
            const palette = shiftColors[colorKey];
            backgroundColor =
                state === PlannedWorkShiftState.InPlanning
                    ? palette.light
                    : palette.dark;
        }
    } else {
        if (!shift.employeeId) {
            backgroundColor = "#e0e0e0";
        } else if (state === PlannedWorkShiftState.InPlanning) {
            backgroundColor = "warning.light";
        } else if (state === PlannedWorkShiftState.Published) {
            backgroundColor = "green.extraLight";
        }
    }

    const employeeHasConflict = (overlapEntry?.employeeConflicts?.length ?? 0) > 0;
    const vehicleHasConflict = (overlapEntry?.vehicleConflicts?.length ?? 0) > 0;

    const overflowLeft = startIsInRange ? undefined : `-${horizontalPadding}px`;

    const cardContent = (
        <Box
            p={isCompact ? 0.5 : 1}
            onClick={onClick}
            data-shift-cell="true"
            sx={{
                boxShadow: isSelected ? "0 0 0 1px #2196f3" : "none",
                height: "100%",
                cursor: onClick ? "pointer" : "default",
                backgroundColor,
                opacity: state === PlannedWorkShiftState.Published ? 1 : 0.7,
                userSelect: "none",
                WebkitUserSelect: "none",
                MozUserSelect: "none",
                msUserSelect: "none",
                marginLeft: overflowLeft,
                marginRight: endIsInRange ? undefined : "-2rem",
                ...(hasConflict && {
                    border: "2px solid",
                    borderColor: "error.main",
                }),
                ...(isMultiDayShift && {
                    borderRight: "3px dashed rgba(0, 0, 0, 0.3)",
                    position: "relative",
                    marginRight: "-8px",
                }),
            }}
        >
            {isCompact ? (
                <ShiftCardCompact
                    shift={shift}
                    timeLabel={timeLabel}
                    onTimeSave={(s, e) =>
                        void submit({ ...shift, startTime: s, endTime: e })
                    }
                    menuItems={menuItems}
                    isRecurring={isRecurring}
                    recurrenceLabel={recurrenceLabel}
                    employeeHasConflict={employeeHasConflict}
                    vehicleHasConflict={vehicleHasConflict}
                    onSaveEmployee={(val) => handleSave(val, "employeeId")}
                    onSaveVehicle={(val) => handleSave(val, "vehicleId")}
                    onSaveRoute={(val) => handleSave(val, "routeId")}
                    onOpenRecurrenceEditor={handleOpenRecurrenceEditor}
                    actualShiftsDuration={actualShiftsDuration}
                    plannedDuration={plannedDuration}
                />
            ) : (
                <Stack spacing={0.5}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="flex-start"
                    >
                        <Box
                            flex={1}
                            sx={{
                                mr: isMultiDayShift ? 0 : 1,
                                whiteSpace: isMultiDayShift
                                    ? "nowrap"
                                    : "normal",
                            }}
                        >
                            <TimePickerInlineEdit
                                label={timeLabel}
                                start={shift.startTime}
                                end={shift.endTime}
                                handleSave={(startTime, endTime) =>
                                    void submit({
                                        ...shift,
                                        startTime,
                                        endTime,
                                    })
                                }
                            />
                        </Box>

                        <Box flexShrink={0}>
                            <MuiMenu
                                id={`workshift-actions-${shift.id}`}
                                label={<MoreVertIcon fontSize="small" />}
                                size="small"
                                items={menuItems}
                                buttonSx={{ p: 0.5 }}
                                noBox
                            />
                        </Box>
                    </Box>

                    <Stack
                        sx={{ pb: 0.5 }}
                        direction="row"
                        gap={0.5}
                        alignItems="center"
                    >
                        <WorkShiftChips
                            plannedShiftId={shift.id}
                            actualShiftsDuration={actualShiftsDuration}
                            plannedDuration={plannedDuration}
                        />
                    </Stack>

                    <Box mt={1}>
                        <TextInlineEdit
                            value={shift.label}
                            handleSave={(val) => handleSave(val, "label")}
                        />
                    </Box>
                    <EmployeeEditor
                        shift={shift}
                        handleSave={(val: string | null) =>
                            handleSave(val, "employeeId")
                        }
                        employeeHasConflict={employeeHasConflict}
                    />
                    <VehicleEditor
                        shift={shift}
                        handleSave={(val: string | null) =>
                            handleSave(val, "vehicleId")
                        }
                        vehicleHasConflict={vehicleHasConflict}
                    />
                    <RouteEditor
                        shift={shift}
                        handleSave={(val: string | null) =>
                            handleSave(val, "routeId")
                        }
                        onCreateRoute={() => setCreateRouteOpen(true)}
                    />

                    {isRecurring ? (
                        <Box display="flex" justifyContent="flex-end">
                            <Tooltip title={recurrenceLabel}>
                                <Box
                                    position="relative"
                                    display="inline-block"
                                    role="button"
                                    aria-label={t(
                                        "workShiftPlanning.recurring.openEditor"
                                    )}
                                    sx={{ cursor: "pointer" }}
                                    onClick={(e) =>
                                        handleOpenRecurrenceEditor(e)
                                    }
                                >
                                    <LoopIcon sx={{ color: "text.disabled" }} />
                                    {shift.isOverridenFromRecurrence ? (
                                        <Box
                                            position="absolute"
                                            top="50%"
                                            left={0}
                                            width="100%"
                                            height="2px"
                                            bgcolor="text.disabled"
                                            style={{
                                                transform: "rotate(35deg)",
                                            }}
                                        />
                                    ) : null}
                                </Box>
                            </Tooltip>
                        </Box>
                    ) : null}
                </Stack>
            )}
        </Box>
    );

    return (
        <>
            <LoadingIndicator loading={loading} />
            <div
                id={`timeline-grid-shift-${shift.id}`}
                ref={containerRef}
                className="timeline-grid-cell timeline-grid-shift"
                style={{
                    position: "absolute",
                    top: topOffset,
                    left: columnMetrics.left,
                    width: columnMetrics.width,
                    padding: `${GRID_CELL_VERTICAL_PADDING}px ${horizontalPadding}px`,
                    boxSizing: "border-box",
                    pointerEvents: "auto",
                }}
            >
                {hasConflict && overlapTooltip ? (
                    <Tooltip
                        placement="left"
                        title={<span style={{ whiteSpace: "pre-line" }}>{overlapTooltip}</span>}
                        {...tooltipProps}
                    >
                        <div>{cardContent}</div>
                    </Tooltip>
                ) : (
                    cardContent
                )}
            </div>

            <Menu
                open={!!recurrenceMenuEl}
                anchorEl={recurrenceMenuEl}
                onClose={handleCloseRecurrenceMenu}
                anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                transformOrigin={{ vertical: "top", horizontal: "right" }}
            >
                {Object.values(RecurrenceAction).map((action) => (
                    <MenuItem key={action} onClick={() => handleSelectRecurrence(action)}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                            <EditIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText
                            primary={t(`workShiftPlanning.recurring.recurrenceActionLabel.${action}`)}
                        />
                    </MenuItem>
                ))}
            </Menu>
            <RouteDialog
                open={createRouteOpen}
                item={newRouteInitial}
                enableIsActive
                onClose={() => setCreateRouteOpen(false)}
                onSaved={(route) => { void handleSave(route.id, "routeId"); setCreateRouteOpen(false); }}
            />
            <RecurrenceDialog
                open={recurrenceDialogOpen}
                onClose={() => setRecurrenceDialogOpen(false)}
                formik={recurrenceFormik}
                onSave={() => void recurrenceFormik.submitForm()}
            />
        </>
    );
};
